import { useEffect, useMemo, useState } from "react";
import { App, Alert, Button, Checkbox, Drawer, Empty, Select, Skeleton, Tag, Tooltip, Typography } from "antd";
import {
  TbAlertTriangle,
  TbChevronDown,
  TbChevronRight,
  TbCirclePlus,
  TbPencil,
  TbPlayerPlay,
  TbTrash,
  TbUnlink,
} from "react-icons/tb";
import { ApiService } from "api/api.service";
import { Resource } from "dtos/stage.dto";
import {
  ChangedTaskRef,
  DriftEntry,
  DriftSelection,
  GroupRef,
  OrphanPolicy,
  RemovedTaskRef,
  TaskRef,
} from "dtos/tasks/task-board.dto";
import { accentTag, neutralTag, taskUi } from "components/TaskBoard/design";
import { TaskStatus } from "dtos/tasks/template.dto";

const warnTag = { ...neutralTag, color: "#b45309", background: "#fffbeb", borderColor: "#fde68a" };

const ORPHAN_OPTIONS: { value: OrphanPolicy; label: string }[] = [
  { value: "removeUntouched", label: "Remove tasks with no progress" },
  { value: "keep", label: "Keep every removed task" },
  { value: "removeAll", label: "Remove all, including completed" },
];

const FIELD_LABEL: Record<string, string> = {
  name: "name",
  description: "description",
  relatedStage: "stage",
  order: "position",
  group: "group",
  parent: "parent",
  previous: "order in chain",
  activationCriteria: "activation rule",
  completionCriteria: "completion rule",
  members: "member slots",
  meta: "metadata",
};

const entryKey = (entry: DriftEntry) =>
  entry.kind === "missing"
    ? `create:${entry.template._id}`
    : entry.kind === "drifted"
      ? `sync:${entry.board._id}`
      : entry.kind === "current"
        ? `current:${entry.board._id}`
        : `detached:${entry.board._id}`;

const Section = ({
  icon,
  title,
  tone = "neutral",
  children,
}: {
  icon: React.ReactNode;
  title: string;
  tone?: "neutral" | "warning";
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-1.5">
    <div className="flex items-center gap-1.5">
      {icon}
      <Typography.Text
        style={{
          fontSize: 11,
          fontWeight: 650,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: tone === "warning" ? "#b45309" : taskUi.textFaint,
        }}
      >
        {title}
      </Typography.Text>
    </div>
    <div className="flex flex-col gap-1">{children}</div>
  </div>
);

const Line = ({ label, trailing }: { label: string; trailing?: React.ReactNode }) => (
  <div
    className="flex items-center gap-2 border border-solid"
    style={{ padding: "6px 9px", borderRadius: 6, borderColor: "#e8e8ee", background: "#fff" }}
  >
    <Typography.Text style={{ fontSize: 12.5, flexGrow: 1 }} ellipsis>
      {label}
    </Typography.Text>
    {trailing}
  </div>
);

interface BoardSyncDrawerProps {
  open: boolean;
  onClose: () => void;
  selector: string;
  resource: Resource;
}

export const BoardSyncDrawer = ({ open, onClose, selector, resource }: BoardSyncDrawerProps) => {
  const { message } = App.useApp();

  const drift = ApiService.taskBoard.useGetDrift(selector, resource, open);
  const apply = ApiService.taskBoard.useApplyDrift(selector, resource);

  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [orphans, setOrphans] = useState<Record<string, OrphanPolicy>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [showCurrent, setShowCurrent] = useState(false);

  const entries = drift.data?.entries ?? [];

  const actionable = useMemo(
    () => entries.filter((entry) => entry.kind === "missing" || entry.kind === "drifted"),
    [entries],
  );

  const current = entries.filter((entry) => entry.kind === "current");
  const detached = entries.filter((entry) => entry.kind === "detached");

  // Everything actionable starts selected — the common case is "apply the template".
  useEffect(() => {
    if (!open) return;
    setSelected(Object.fromEntries(actionable.map((entry) => [entryKey(entry), true])));
    setExpanded({});
  }, [open, drift.dataUpdatedAt]);

  const selections: DriftSelection[] = useMemo(
    () =>
      actionable
        .filter((entry) => selected[entryKey(entry)])
        .map((entry) =>
          entry.kind === "missing"
            ? {
                action: "create" as const,
                template: String(entry.template._id),
                templateUpdatedAt: entry.template.updatedAt,
              }
            : {
                action: "sync" as const,
                template: String(entry.template._id),
                board: String(entry.board._id),
                templateUpdatedAt: entry.template.updatedAt,
                orphans: orphans[entryKey(entry)],
              },
        ),
    [actionable, selected, orphans],
  );

  const onApply = () => {
    apply.mutate(
      { selections },
      {
        onSuccess: (result) => {
          if (result.failed.length === 0) {
            void message.success(`${result.applied.length} board${result.applied.length === 1 ? "" : "s"} updated`);
            onClose();
            return;
          }

          void message.warning(
            `${result.applied.length} applied, ${result.failed.length} failed — ${result.failed[0].error}`,
          );
        },
        onError: () => void message.error("Could not apply the template updates"),
      },
    );
  };

  const renderDrifted = (entry: Extract<DriftEntry, { kind: "drifted" }>) => {
    const key = entryKey(entry);
    const isOpen = expanded[key] ?? false;
    const policy = orphans[key] ?? "removeUntouched";
    const removable = entry.tasksRemoved.length + entry.orphansKept.length;

    const headline = [
      entry.tasksAdded.length ? `${entry.tasksAdded.length} new` : null,
      entry.tasksChanged.length ? `${entry.tasksChanged.length} changed` : null,
      removable ? `${removable} removed` : null,
      entry.groupsAdded.length
        ? `${entry.groupsAdded.length} new group${entry.groupsAdded.length === 1 ? "" : "s"}`
        : null,
    ]
      .filter(Boolean)
      .join(" · ");

    return (
      <div
        key={key}
        className="border border-solid"
        style={{ borderColor: taskUi.border, borderRadius: taskUi.radius, background: "#fff" }}
      >
        <div className="flex items-start gap-3" style={{ padding: "12px 14px" }}>
          <Checkbox
            checked={selected[key] ?? false}
            onChange={(e) => setSelected((prev) => ({ ...prev, [key]: e.target.checked }))}
            style={{ marginTop: 2 }}
          />

          <div className="flex min-w-0 flex-grow flex-col gap-1">
            <div className="flex items-center gap-2">
              <Typography.Text strong style={{ fontSize: 13.5 }} ellipsis>
                {entry.board.name}
              </Typography.Text>
              <Tag bordered style={accentTag}>
                behind template
              </Tag>
            </div>

            <Typography.Text style={{ fontSize: 12, color: taskUi.textFaint }}>{headline}</Typography.Text>

            {entry.assigneesAtRisk.length > 0 && (
              <Typography.Text style={{ fontSize: 11.5, color: "#b45309" }}>
                {entry.assigneesAtRisk.length} task{entry.assigneesAtRisk.length === 1 ? "" : "s"} will lose their
                assigned person because the template repointed the slot
              </Typography.Text>
            )}
          </div>

          <Button
            type="text"
            size="small"
            icon={isOpen ? <TbChevronDown size={14} /> : <TbChevronRight size={14} />}
            onClick={() => setExpanded((prev) => ({ ...prev, [key]: !isOpen }))}
          >
            {isOpen ? "Hide" : "Review"}
          </Button>
        </div>

        {isOpen && (
          <div
            className="flex flex-col gap-4 border-0 border-t border-solid"
            style={{ padding: "12px 14px 14px 41px", borderColor: taskUi.hairline, background: taskUi.subtle }}
          >
            {entry.groupsAdded.length > 0 && (
              <Section icon={<TbCirclePlus size={13} color={taskUi.indigo} />} title="New groups">
                {entry.groupsAdded.map((group: GroupRef) => (
                  <Line key={String(group._id)} label={group.name} />
                ))}
              </Section>
            )}

            {entry.tasksAdded.length > 0 && (
              <Section icon={<TbCirclePlus size={13} color={taskUi.indigo} />} title="New tasks">
                {entry.tasksAdded.map((task: TaskRef) => (
                  <Line key={String(task._id)} label={task.name} />
                ))}
              </Section>
            )}

            {entry.tasksChanged.length > 0 && (
              <Section icon={<TbPencil size={13} color={taskUi.textFaint} />} title="Changed">
                {entry.tasksChanged.map((task: ChangedTaskRef) => (
                  <Line
                    key={String(task._id)}
                    label={task.name}
                    trailing={
                      <span className="flex flex-shrink-0 flex-wrap gap-1">
                        {task.fields.map((field) => (
                          <Tag key={field} bordered style={neutralTag}>
                            {FIELD_LABEL[field] ?? field}
                          </Tag>
                        ))}
                      </span>
                    }
                  />
                ))}
              </Section>
            )}

            {entry.tasksRemoved.length > 0 && (
              <Section icon={<TbTrash size={13} color={taskUi.textFaint} />} title="No longer in the template">
                {entry.tasksRemoved.map((task: RemovedTaskRef) => (
                  <Line key={String(task._id)} label={task.name} />
                ))}
              </Section>
            )}

            {entry.orphansKept.length > 0 && (
              <Section
                icon={<TbAlertTriangle size={13} color="#b45309" />}
                title="Removed from the template but already worked"
                tone="warning"
              >
                {entry.orphansKept.map((task: RemovedTaskRef) => (
                  <Line
                    key={String(task._id)}
                    label={task.name}
                    trailing={
                      <Tag bordered style={warnTag}>
                        {task.status === TaskStatus.COMPLETE ? "complete" : task.status.toLowerCase()}
                      </Tag>
                    }
                  />
                ))}
              </Section>
            )}

            {entry.activated.length > 0 && (
              <Section icon={<TbPlayerPlay size={13} color="#16a34a" />} title="Will become active">
                {entry.activated.map((task: TaskRef) => (
                  <Line key={String(task._id)} label={task.name} />
                ))}
              </Section>
            )}

            {removable > 0 && (
              <div className="flex flex-col gap-1.5">
                <Typography.Text style={{ fontSize: 11.5, color: taskUi.textFaint }}>
                  Tasks the template removed
                </Typography.Text>
                <Select
                  size="small"
                  style={{ width: 280 }}
                  value={policy}
                  options={ORPHAN_OPTIONS}
                  onChange={(value) => setOrphans((prev) => ({ ...prev, [key]: value }))}
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderMissing = (entry: Extract<DriftEntry, { kind: "missing" }>) => {
    const key = entryKey(entry);

    return (
      <div
        key={key}
        className="flex items-start gap-3 border border-solid"
        style={{ padding: "12px 14px", borderColor: taskUi.border, borderRadius: taskUi.radius, background: "#fff" }}
      >
        <Checkbox
          checked={selected[key] ?? false}
          onChange={(e) => setSelected((prev) => ({ ...prev, [key]: e.target.checked }))}
          style={{ marginTop: 2 }}
        />
        <div className="flex min-w-0 flex-grow flex-col gap-1">
          <div className="flex items-center gap-2">
            <Typography.Text strong style={{ fontSize: 13.5 }} ellipsis>
              {entry.template.name}
            </Typography.Text>
            {entry.template.autoStart && (
              <Tooltip title="This template starts automatically on new applications">
                <Tag bordered style={warnTag}>
                  should be here
                </Tag>
              </Tooltip>
            )}
          </div>
          <Typography.Text style={{ fontSize: 12, color: taskUi.textFaint, fontVariantNumeric: "tabular-nums" }}>
            No board on this application · {entry.summary.groups} group
            {entry.summary.groups === 1 ? "" : "s"} · {entry.summary.tasks} task
            {entry.summary.tasks === 1 ? "" : "s"}
          </Typography.Text>
        </div>
        <Tag bordered style={accentTag}>
          will be created
        </Tag>
      </div>
    );
  };

  const selectedCount = selections.length;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={620}
      title="Template updates"
      styles={{ body: { background: taskUi.layout, padding: 16 } }}
      footer={
        <div className="flex items-center gap-2" style={{ padding: "4px 0" }}>
          <Typography.Text style={{ fontSize: 12.5, color: taskUi.textFaint, flexGrow: 1 }}>
            {selectedCount === 0
              ? "Nothing selected"
              : `${selectedCount} board${selectedCount === 1 ? "" : "s"} will be updated`}
          </Typography.Text>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" disabled={selectedCount === 0} loading={apply.isPending} onClick={onApply}>
            Apply
          </Button>
        </div>
      }
    >
      {drift.isLoading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : drift.isError ? (
        <Alert type="error" showIcon message="Could not compare this application with its templates" />
      ) : actionable.length === 0 && detached.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Every board matches its template" />
      ) : (
        <div className="flex flex-col gap-3 px-4 py-2">
          {actionable.map((entry) =>
            entry.kind === "missing"
              ? renderMissing(entry)
              : renderDrifted(entry as Extract<DriftEntry, { kind: "drifted" }>),
          )}

          {detached.map((entry) => {
            const detachedEntry = entry as Extract<DriftEntry, { kind: "detached" }>;

            return (
              <div
                key={entryKey(entry)}
                className="flex items-start gap-3 border border-solid"
                style={{
                  padding: "12px 14px",
                  borderColor: "#f5c6c0",
                  borderRadius: taskUi.radius,
                  background: "#fef8f7",
                }}
              >
                <TbUnlink size={15} color="#b42318" style={{ marginTop: 2, flexShrink: 0 }} />
                <div className="flex min-w-0 flex-col gap-0.5">
                  <Typography.Text strong style={{ fontSize: 13.5 }}>
                    {detachedEntry.board.name}
                  </Typography.Text>
                  <Typography.Text style={{ fontSize: 12, color: "#7f2a21" }}>
                    {detachedEntry.reason}. It cannot be kept in step with a template.
                  </Typography.Text>
                </div>
              </div>
            );
          })}

          {current.length > 0 && (
            <div className="flex flex-col gap-2">
              <Button
                type="text"
                size="small"
                style={{ alignSelf: "flex-start", color: taskUi.textFaint, paddingLeft: 0 }}
                icon={showCurrent ? <TbChevronDown size={13} /> : <TbChevronRight size={13} />}
                onClick={() => setShowCurrent((prev) => !prev)}
              >
                {current.length} board{current.length === 1 ? "" : "s"} already up to date
              </Button>

              {showCurrent && (
                <div
                  className="overflow-hidden border border-solid"
                  style={{ borderColor: taskUi.border, borderRadius: taskUi.radius, background: "#fff" }}
                >
                  {current.map((entry, i) => {
                    const currentEntry = entry as Extract<DriftEntry, { kind: "current" }>;

                    return (
                      <div
                        key={entryKey(entry)}
                        className="flex items-center gap-2 border-0 border-solid"
                        style={{
                          padding: "9px 12px",
                          borderTopWidth: i === 0 ? 0 : 1,
                          borderColor: taskUi.hairline,
                        }}
                      >
                        <Typography.Text style={{ fontSize: 12.5, flexGrow: 1 }} ellipsis>
                          {currentEntry.board.name}
                        </Typography.Text>
                        <Typography.Text style={{ fontSize: 11.5, color: taskUi.textFaint }}>
                          {currentEntry.template.name}
                        </Typography.Text>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
};
